import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX } from 'react-icons/fi';

const DepartmentsJobTitles = () => {
  const { success, error: showError } = useToast();
  const [tab, setTab] = useState('Departments');
  const [departments, setDepartments] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [deptForm, setDeptForm] = useState({ name: '', costCentreCode: '', parentDepartmentId: '' });
  const [titleForm, setTitleForm] = useState({ title: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const loadAll = () => {
    setLoading(true);
    Promise.all([apiClient.get('/hr/departments'), apiClient.get('/hr/job-titles')])
      .then(([deptRes, titleRes]) => {
        setDepartments(deptRes.data.data);
        setJobTitles(titleRes.data.data);
      })
      .catch((err) => showError(getErrorMessage(err, 'Could not load data')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, []); // eslint-disable-line react-hooks/exhaustive-deps

  const departmentName = (id) => departments.find((d) => d.id === id)?.name || '—';

  const handleAddDept = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...deptForm };
      if (!payload.costCentreCode) delete payload.costCentreCode;
      if (!payload.parentDepartmentId) delete payload.parentDepartmentId;
      await apiClient.post('/hr/departments', payload);
      success('✅ Department added');
      setShowForm(false);
      setDeptForm({ name: '', costCentreCode: '', parentDepartmentId: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not add department')); }
  };

  const handleAddTitle = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/hr/job-titles', titleForm);
      success('✅ Job title added');
      setShowForm(false);
      setTitleForm({ title: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not add job title')); }
  };

  const startEdit = (item, type) => {
    setEditingId(item.id);
    if (type === 'dept') setEditForm({ name: item.name, costCentreCode: item.costCentreCode || '', isActive: item.isActive });
    else setEditForm({ title: item.title, isActive: item.isActive });
  };

  const handleSaveDept = async (e, id) => {
    e.preventDefault();
    try {
      await apiClient.patch(`/hr/departments/${id}`, editForm);
      success('✅ Department updated');
      setEditingId(null);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not update department')); }
  };

  const handleSaveTitle = async (e, id) => {
    e.preventDefault();
    try {
      await apiClient.patch(`/hr/job-titles/${id}`, editForm);
      success('✅ Job title updated');
      setEditingId(null);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not update job title')); }
  };

  const handleDeleteDept = async (id) => {
    if (!window.confirm('Delete this department?')) return;
    try {
      await apiClient.delete(`/hr/departments/${id}`);
      success('✅ Department deleted');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not delete department')); }
  };

  const handleDeleteTitle = async (id) => {
    if (!window.confirm('Delete this job title?')) return;
    try {
      await apiClient.delete(`/hr/job-titles/${id}`);
      success('✅ Job title deleted');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not delete job title')); }
  };

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Departments & Job Titles</h1>
        <p>Organisational structure used across Employees, Recruitment, and Payroll</p>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
        {['Departments', 'Job Titles'].map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setShowForm(false); setEditingId(null); }}
            style={{
              padding: '0.75rem 1rem', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: 600, color: tab === t ? '#3b82f6' : '#6b7280',
              borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Departments' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Departments</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'Add Department'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleAddDept} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field"><label>Name *</label><input required value={deptForm.name} onChange={(e) => setDeptForm(p => ({ ...p, name: e.target.value }))} /></div>
              <div className="form-field"><label>Cost Centre Code</label><input value={deptForm.costCentreCode} onChange={(e) => setDeptForm(p => ({ ...p, costCentreCode: e.target.value }))} /></div>
              <div className="form-field">
                <label>Parent Department</label>
                <select value={deptForm.parentDepartmentId} onChange={(e) => setDeptForm(p => ({ ...p, parentDepartmentId: e.target.value }))}>
                  <option value="">-- none --</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Add</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Name</th><th>Cost Centre</th><th>Parent</th><th>Active</th><th>Actions</th></tr></thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
                ) : departments.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No departments yet</td></tr>
                ) : departments.map((d) => (
                  <tr key={d.id}>
                    {editingId === d.id ? (
                      <td colSpan={5}>
                        <form onSubmit={(e) => handleSaveDept(e, d.id)} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.5rem 0' }}>
                          <input value={editForm.name} onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))} style={{ flex: 1, padding: '0.4rem' }} />
                          <input value={editForm.costCentreCode} onChange={(e) => setEditForm(p => ({ ...p, costCentreCode: e.target.value }))} placeholder="Cost centre" style={{ width: '120px', padding: '0.4rem' }} />
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', textTransform: 'none' }}>
                            <input type="checkbox" checked={editForm.isActive} onChange={(e) => setEditForm(p => ({ ...p, isActive: e.target.checked }))} style={{ width: 'auto' }} /> Active
                          </label>
                          <button type="submit" className="action-icon edit" title="Save"><FiSave size={16} /></button>
                          <button type="button" className="action-icon delete" title="Cancel" onClick={() => setEditingId(null)}><FiX size={16} /></button>
                        </form>
                      </td>
                    ) : (
                      <>
                        <td style={{ fontWeight: 600 }}>{d.name}</td>
                        <td>{d.costCentreCode || '—'}</td>
                        <td>{d.parentDepartmentId ? departmentName(d.parentDepartmentId) : '—'}</td>
                        <td><span className="badge" style={{ background: d.isActive ? '#10b981' : '#9ca3af', color: 'white' }}>{d.isActive ? 'Yes' : 'No'}</span></td>
                        <td>
                          <div className="actions">
                            <button className="action-icon edit" title="Edit" onClick={() => startEdit(d, 'dept')}><FiEdit2 size={16} /></button>
                            <button className="action-icon delete" title="Delete" onClick={() => handleDeleteDept(d.id)}><FiTrash2 size={16} /></button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Job Titles' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Job Titles</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'Add Job Title'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleAddTitle} style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
              <input required value={titleForm.title} onChange={(e) => setTitleForm({ title: e.target.value })} placeholder="Job title" style={{ flex: 1, padding: '0.5rem' }} />
              <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Add</button>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Title</th><th>Active</th><th>Actions</th></tr></thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={3} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
                ) : jobTitles.length === 0 ? (
                  <tr><td colSpan={3} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No job titles yet</td></tr>
                ) : jobTitles.map((t) => (
                  <tr key={t.id}>
                    {editingId === t.id ? (
                      <td colSpan={3}>
                        <form onSubmit={(e) => handleSaveTitle(e, t.id)} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.5rem 0' }}>
                          <input value={editForm.title} onChange={(e) => setEditForm(p => ({ ...p, title: e.target.value }))} style={{ flex: 1, padding: '0.4rem' }} />
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', textTransform: 'none' }}>
                            <input type="checkbox" checked={editForm.isActive} onChange={(e) => setEditForm(p => ({ ...p, isActive: e.target.checked }))} style={{ width: 'auto' }} /> Active
                          </label>
                          <button type="submit" className="action-icon edit" title="Save"><FiSave size={16} /></button>
                          <button type="button" className="action-icon delete" title="Cancel" onClick={() => setEditingId(null)}><FiX size={16} /></button>
                        </form>
                      </td>
                    ) : (
                      <>
                        <td style={{ fontWeight: 600 }}>{t.title}</td>
                        <td><span className="badge" style={{ background: t.isActive ? '#10b981' : '#9ca3af', color: 'white' }}>{t.isActive ? 'Yes' : 'No'}</span></td>
                        <td>
                          <div className="actions">
                            <button className="action-icon edit" title="Edit" onClick={() => startEdit(t, 'title')}><FiEdit2 size={16} /></button>
                            <button className="action-icon delete" title="Delete" onClick={() => handleDeleteTitle(t.id)}><FiTrash2 size={16} /></button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </motion.div>
  );
};

export default DepartmentsJobTitles;